import {
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
} from 'firebase/firestore'
import type { DocumentData, QueryDocumentSnapshot } from 'firebase/firestore'
import { db } from '../lib/firebase'
import { timestampToIso } from './customersService'
import { deleteImage, uploadImages, validateImage } from './imageService'
import type { Enquiry, EnquiryInput } from '../types/admin'

export const enquiriesRef = collection(db, 'enquiries')

/** Midlertidig status mens billederne uploades. Vises ikke i admin. */
export const UPLOADING_STATUS = 'Uploader'

function toEnquiry(snapshot: QueryDocumentSnapshot<DocumentData>): Enquiry {
  const data = snapshot.data()
  return {
    id: snapshot.id,
    name: data.name ?? '',
    phone: data.phone ?? '',
    email: data.email ?? '',
    address: data.address ?? '',
    description: data.description ?? '',
    status: data.status ?? 'Ny henvendelse',
    images: Array.isArray(data.images) ? data.images : [],
    createdAt: timestampToIso(data.createdAt),
    caseId: data.caseId ?? undefined,
  }
}

/**
 * Lytter på henvendelser. Ufærdige henvendelser (status "Uploader") filtreres fra,
 * så halvfærdige indsendelser aldrig dukker op som rigtige henvendelser i admin.
 */
export function subscribeEnquiries(
  onChange: (enquiries: Enquiry[]) => void,
  onError: (error: unknown) => void,
): () => void {
  return onSnapshot(
    query(enquiriesRef, orderBy('createdAt', 'desc')),
    (snapshot) =>
      onChange(
        snapshot.docs
          .map(toEnquiry)
          .filter((enquiry) => String(enquiry.status) !== UPLOADING_STATUS),
      ),
    onError,
  )
}

export interface SubmitResult {
  /** Dokument-id'et. Genbruges ved nyt forsøg, så der ikke opstår dubletter. */
  draftId: string
}

/**
 * Sikker indsendelse i faste trin:
 * 1) validér alle billeder — intet oprettes hvis en fil er ugyldig
 * 2) reservér dokument-id'et lokalt (uden at skrive endnu)
 * 3) upload billederne til enquiries/{enquiryId}/{fileName}
 * 4) opret dokumentet ÉN gang med stierne og status "Ny henvendelse"
 *
 * Dokumentet skrives kun én gang, fordi Firestore-reglerne tillader offentlig
 * "create", men ikke "update". Samme id genbruges ved nyt forsøg, så et
 * mislykket upload aldrig kan give dubletter i admin.
 */
export async function submitEnquiry(
  input: EnquiryInput,
  existingDraftId?: string,
): Promise<SubmitResult> {
  for (const file of input.files) {
    const problem = validateImage(file)
    if (problem) throw new Error(problem)
  }

  // Firestore-id'et kendes før dokumentet oprettes og bruges som enquiryId i Storage.
  const enquiryRef = existingDraftId ? doc(db, 'enquiries', existingDraftId) : doc(enquiriesRef)

  let uploaded: { path: string; url: string; name: string }[] = []
  if (input.files.length > 0) {
    try {
      // Offentlig upload: ingen login, ingen getDownloadURL (kun admin må læse).
      uploaded = await uploadImages(input.files, `enquiries/${enquiryRef.id}`)
    } catch (error) {
      const failure = new Error('upload-failed') as Error & { draftId: string; cause?: unknown }
      failure.draftId = enquiryRef.id
      failure.cause = error
      throw failure
    }
  }

  try {
    await setDoc(enquiryRef, {
      name: input.name.trim(),
      phone: input.phone.trim(),
      email: input.email.trim(),
      address: input.address.trim(),
      description: input.description.trim(),
      status: 'Ny henvendelse',
      images: uploaded,
      imagePaths: uploaded.map((image) => image.path),
      createdAt: serverTimestamp(),
    })
  } catch (error) {
    const failure = new Error('save-failed') as Error & { draftId: string; cause?: unknown }
    failure.draftId = enquiryRef.id
    failure.cause = error
    throw failure
  }

  return { draftId: enquiryRef.id }
}

export async function updateEnquiryDoc(id: string, patch: Partial<Enquiry>): Promise<void> {
  await updateDoc(doc(db, 'enquiries', id), { ...patch, updatedAt: serverTimestamp() })
}

/** Sletter henvendelsen og alle dens billeder i Storage. */
export async function deleteEnquiryDoc(id: string, imagePaths: string[]): Promise<void> {
  await Promise.all(imagePaths.map((path) => deleteImage(path).catch(() => null)))
  await deleteDoc(doc(db, 'enquiries', id))
}
